import { EthingsRestClient } from '@eagle/ethings-rest-client';
import { Theme } from '@ethings/ui-toolkit';
import { ThemeSettings } from 'sisense-sdk-ui-next';
import { I18n } from '../providers';

const SISENSE_SUPPORTED_LANGUAGES: Set<string> = new Set([
  'de-DE',  // German
  'en-US',  // English (United States)
  'es-AR',  // Spanish (Argentina)
  'es-ES',  // Spanish (Spain)
  'fr-FR',  // French
  'it-IT',  // Italian
  'ja-JP',  // Japanese
  'ko-KR',  // Korean
  'nl-NL',  // Dutch (Netherlands)
  'pt-BR',  // Portuguese
  'ru-RU',  // Russian
  'tr-TR',  // Turkish
  'zh-CN',  // Chinese
]);

const SPECIAL_CASES: Record<string, string> = {
  'es-MX': 'es-ES', // Spanish (Mexico) --> Spanish (Spain)
};

// Returns a supported language code for sisense EmbedSDK
// The language returned is dependent on the user-selected locale, falling back to browser language if necessary
export const getSisenseLanguageCode = (): string => {
  const userLocale = I18n.getInstance().language?.replace('_', '-') ?? 'en-US';

  // Apply Special Cases if it exists
  const languageCode = SPECIAL_CASES[userLocale] || userLocale;

  // Return the supported language if it exists, else fall back to 'en-US'
  return SISENSE_SUPPORTED_LANGUAGES.has(languageCode) ? languageCode : 'en-US';
};

export const fetchWatToken = async (restClient: EthingsRestClient): Promise<{ wat: string }> => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  return restClient.sisense.wat({ timeZone });
};

export const createSisenseTheme = (theme: Theme): ThemeSettings => ({
  chart: {
    textColor: theme.palette.text.primary,
    secondaryTextColor: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
  },
  /*
  palette: {
    variantColors: theme.chartColors,
  },
  */
  general: {
    brandColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.default,
    primaryButtonTextColor: theme.palette.primary.contrastText,
    primaryButtonHoverColor: theme.palette.primary.dark,
    popover: {
      cornerRadius: theme.shape.borderRadius,
      shadow: theme.shadows?.[1],
      header: {
        backgroundColor: theme.palette.background.paper,
        textColor: theme.palette.text.primary,
      },
      footer: {
        backgroundColor: theme.palette.background.paper,
        textColor: theme.palette.text.secondary,
      },
      content: {
        backgroundColor: theme.palette.background.paper,
        textColor: theme.palette.text.primary,
      },
    },
    buttons: {
      cancel: {
        backgroundColor: theme.palette.grey?.[200] || theme.palette.background.paper,
        textColor: theme.palette.text.primary,
      },
    },
  },
  typography: {
    fontFamily: theme.typography.fontFamily,
    primaryTextColor: theme.palette.text.primary,
    secondaryTextColor: theme.palette.text.secondary,
    hyperlinkColor: theme.palette.primary.main,
  },
  widget: {
    spaceAround: 'Large',
    cornerRadius: 'Medium',
    shadow: 'Light',
    border: false,
    borderColor: theme.palette.divider,
    header: {
      titleFontSize: typeof theme.typography.h6?.fontSize === 'number' ? theme.typography.h6.fontSize : undefined,
      titleTextColor: theme.palette.text.primary,
      dividerLine: false,
      dividerLineColor: theme.palette.background.paper,
      backgroundColor: theme.palette.background.paper,
    },
  },
  dashboard: {
    backgroundColor: theme.palette.background.default,
    dividerLineColor: theme.palette.divider,
    borderColor: theme.palette.divider,
    toolbar: {
      primaryTextColor: theme.palette.text.primary,
      secondaryTextColor: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.paper,
      dividerLineColor: theme.palette.background.paper,
    },
  },
  filter: {
    panel: {
      titleColor: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      dividerLineColor: theme.palette.divider,
      borderColor: theme.palette.divider,
    },
  },
  aiChat: {
    backgroundColor: theme.palette.background.default,
    primaryTextColor: theme.palette.text.primary,
    secondaryTextColor: theme.palette.text.secondary,
    border: 'none',
    borderRadius: `${theme.shape.borderRadius}px`,
    userMessages: {
      backgroundColor: theme.palette.primary.light,
    },
    systemMessages: {
      backgroundColor: theme.palette.background.paper,
    },
    input: {
      backgroundColor: theme.palette.background.paper,
      focus: {
        outlineColor: theme.palette.primary.main,
      },
    },
    header: {
      backgroundColor: theme.palette.primary.main,
      textColor: theme.palette.primary.contrastText,
    },
    dropup: {
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows?.[1],
      borderRadius: `${theme.shape.borderRadius}px`,
      headers: {
        textColor: theme.palette.text.secondary,
        hover: {
          backgroundColor: theme.palette.action.hover,
        },
      },
      items: {
        textColor: theme.palette.text.primary,
        hover: {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    suggestions: {
      textColor: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      border: 'none',
      borderRadius: `${theme.shape.borderRadius}px`,
      hover: {
        textColor: theme.palette.text.primary,
        backgroundColor: theme.palette.action.hover,
      },
      gap: typeof theme.spacing === 'function' ? theme.spacing(1) : '8px',
    },
    clickableMessages: {
      textColor: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      border: 'none',
      hover: {
        textColor: theme.palette.text.primary,
        backgroundColor: theme.palette.action.hover,
      },
    },
    dataTopics: {
      backgroundColor: theme.palette.background.paper,
      items: {
        textColor: theme.palette.text.primary,
        backgroundColor: theme.palette.background.default,
      },
    },
    icons: {
      color: theme.palette.text.secondary,
      feedbackIcons: {
        hoverColor: theme.palette.action.hover,
      },
    },
    tooltips: {
      backgroundColor: theme.tooltip?.background || theme.palette.grey[700],
      textColor: theme.tooltip?.contrastText || theme.palette.common.white,
      boxShadow: theme.shadows?.[1],
    },
  },
});
